/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useEffect } from "react";

import * as Sentry from "@sentry/react";
import { useRouter } from "next/router";
import { isMobile } from "react-device-detect";
import { useSelector } from "react-redux";

import { useCurrentUser } from "src/app/useCurrentUser";
import { Auth } from "src/classes/user/auth/Auth";
import { AppTopbar } from "src/components/AppTopbar";
import { AppHotkeys } from "src/components/Hotkeys/AppHotkeys";
import { AppWrapper } from "src/components/Layout/PageLayout";
import SearchModal from "src/components/Search/SearchModal";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper";
import { FullScreenError } from "src/components/UI/Error/Errors";
import WhatsNew from "src/components/Updating/WhatsNew/WhatsNew";
import { META_ROUTES, AUTH_ROUTES } from "src/lib/auth";
import { AuthLevel } from "src/lib/constants";
import { initDatadog } from "src/lib/logging/datadog";
import { initGTag } from "src/lib/logging/gtag";
import { initLogRocket } from "src/lib/logging/logrocket";
import { identifySegmentUser } from "src/lib/logging/segment";
import { initSentry } from "src/lib/logging/sentry";
import type { AppState } from "src/store";
import { wrapper } from "src/store/store";

const PLATE_ROUTE = "/plate/[username]/[pageSlug]";
const VHTApp = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter();
  const searchModalOpen = useSelector((state: AppState) => state.search.modalOpen);
  const isScreenshotter = useSelector((state: AppState) => state.globalUI.uiSettings.isScreenshotter);
  const currentUser = useCurrentUser();
  const auth = Auth.instance;
  const authLevel = auth.observe((state) => state.authLevel);

  const showTopbar =
    currentUser && !isScreenshotter && ![...META_ROUTES, ...AUTH_ROUTES, PLATE_ROUTE].includes(router.route);

  useEffect(() => {
    const shouldTrack =
      !isScreenshotter && currentUser?.snapshot.id && ["prod", "dev"].includes(process.env.ENVIRONMENT);
    // not screenshotter, with user loaded, on browser (not ssr), and right env
    if (shouldTrack) {
      initLogRocket(currentUser);
      initGTag(currentUser);
      initSentry(currentUser);
      identifySegmentUser(currentUser);
      initDatadog(isScreenshotter);
    }
  }, [
    currentUser?.snapshot.username,
    currentUser?.snapshot.id,
    currentUser?.snapshot.displayName,
    currentUser?.snapshot.email,
  ]);

  return (
    <ConditionalWrapper
      condition={authLevel === AuthLevel.Authorized}
      wrapper={(appChildren: React.ReactNode) => <AppHotkeys>{appChildren}</AppHotkeys>}
    >
      <AppWrapper style={{ height: isMobile ? "unset" : "100vh" }}>
        <Sentry.ErrorBoundary
          fallback={({ error, componentStack, resetError }) => (
            <FullScreenError
              error={error}
              componentStack={componentStack}
              resetError={resetError}
              message="Something went wrong.."
            />
          )}
          showDialog
        >
          {showTopbar && <AppTopbar />}
          {children}
          {searchModalOpen && currentUser && <SearchModal algoliaApiKey={currentUser.snapshot.algoliaApiKey} />}
          {!isMobile && <WhatsNew />}
        </Sentry.ErrorBoundary>
      </AppWrapper>
    </ConditionalWrapper>
  );
};

export default wrapper.withRedux(VHTApp);
